// Repli des comparaisons, tuile par tuile (Olivier, bêta-testeur du Grand Besançon, réunion des
// fondateurs du 14/10 : « trop de chiffres » — sur certaines tuiles il ne veut voir que Besançon,
// ou le Grand Besançon ; Cyrille : « tuile par tuile, chaque tuile doit avoir son petit bouton »).
//
// Les règles pures, à part pour être testées sans rendre une page (comme `masques.ts`) : quels
// graphiques et quelles tuiles PORTENT des comparaisons — échelles (EPCI, département, région,
// France), médiane nationale, communes témoins —, et ce qu'il reste d'un graphique replié.
// Le stockage vit dans `prefs.ts` (au compte, comme les tailles) ; le rendu dans
// `RepliComparaisons.tsx`, `components.tsx` et `ChartBlock.tsx`.
//
// Replier n'efface rien et ne recalcule rien : le graphique replié est le graphique SERVI, réduit
// à la barre ou à la série du territoire consulté. Les exports (image, PDF, CSV) gardent tout.
import type { ChartSpec } from '../observatoire/ChartBlock'
import { construireComparaison } from './comparaison'
import { LIBELLE_MA_COMMUNE } from './tableau/structure'
import type { Kpi } from './types'

/** Où se lisent les territoires d'un graphique : une BARRE par territoire (empilé « commune et
 *  échelles », commune face à la médiane nationale) ou une SÉRIE par territoire (groupé ou
 *  courbe dont la première série est la commune). */
export type AxeTerritoires = 'barres' | 'series'

/** Le libellé de la commune sur l'unique graphique « commune face à la médiane nationale »
 *  (san-3, `builders_vie`) — partout ailleurs le serveur écrit « Ma commune ». */
const LIBELLE_COMMUNE_SEUL = 'Commune'

/** Le graphique servi porte-t-il des comparaisons, et sur quel axe ? `null` : aucune — pas de
 *  bouton. Le repère est le contrat du serveur : le territoire consulté vient EN PREMIER et
 *  s'appelle « Ma commune » (`LIBELLE_MA_COMMUNE`, déjà lu par la vue « Structure »). Deux séries
 *  qui ne sont pas des territoires (public / privé, hommes / femmes) n'en ont donc jamais. La
 *  pyramide n'en a pas : son référent est ajouté par le lecteur et se retire d'un clic. */
export function axeTerritoires(spec: ChartSpec | null | undefined): AxeTerritoires | null {
  if (!spec || (spec.type !== 'bar' && spec.type !== 'line')) return null
  const labels = spec.labels ?? []
  const datasets = spec.datasets ?? []
  if (spec.type === 'bar' && labels.length >= 2 && datasets.length >= 1) {
    const premier = String(labels[0])
    if (spec.empile && premier === LIBELLE_MA_COMMUNE) return 'barres'
    if (!spec.empile && datasets.length === 1
        && (premier === LIBELLE_MA_COMMUNE || premier === LIBELLE_COMMUNE_SEUL)) return 'barres'
  }
  if (!spec.empile && datasets.length >= 2 && datasets[0]?.label === LIBELLE_MA_COMMUNE) return 'series'
  return null
}

/** Le graphique réduit au territoire consulté — une copie, le spec servi n'est jamais modifié.
 *  Sans comparaison, le spec est rendu tel quel (même objet). */
export function replierComparaisons(spec: ChartSpec): ChartSpec {
  const axe = axeTerritoires(spec)
  if (axe === 'series') return { ...spec, datasets: spec.datasets.slice(0, 1) }
  if (axe === 'barres') {
    return {
      ...spec,
      labels: spec.labels.slice(0, 1),
      datasets: spec.datasets.map(d => ({
        ...d, data: d.data.slice(0, 1),
        ...(d.abs ? { abs: d.abs.slice(0, 1) } : {}),
        ...(d.notes ? { notes: d.notes.slice(0, 1) } : {}),
      })),
    }
  }
  return spec
}

/** Une tuile a-t-elle quelque chose à replier ? Des échelles qui s'affichent (au moins une
 *  valeur servie), ou des témoins — un témoin « N/D » s'affiche aussi, il se replie donc. */
export function kpiAComparaisons(k: Kpi): boolean {
  const compare = k.secondaire ?? k
  if (compare.temoins?.length) return true
  return !!compare.comparaison?.length && construireComparaison(compare.comparaison, compare.unit) !== null
}
